// packages/front/src/_common/hooks/use-api-mutation.ts
//
// Thin wrapper over useMutation that uses apiFetch as the mutationFn.
// Callers pass `invalidateKeys` (array of query keys) to auto-invalidate on success,
// plus any additional onSuccess callback. No direct apiFetch in *-page.tsx.

import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { apiFetch } from "../lib/http-client";

export interface UseApiMutationOptions<TBody, TResp>
  extends Omit<
    UseMutationOptions<TResp, Error, TBody, unknown>,
    "mutationFn" | "onSuccess"
  > {
  /** Query keys to invalidate via queryClient.invalidateQueries on success. */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Optional request timeout (ms) forwarded to apiFetch. */
  timeoutMs?: number;
  /** Optional request headers, static or derived from mutation variables. */
  headers?: HeadersInit | ((variables: TBody) => HeadersInit);
  /**
   * Optional extractor for the JSON request body. When omitted, the entire
   * `variables` value is serialized as the body. Use this to separate URL
   * params (e.g. an id embedded in the URL) from the actual request payload.
   */
  body?: (variables: TBody) => unknown;
  onSuccess?: (data: TResp, variables: TBody, onMutateResult: unknown) => void;
}

export function useApiMutation<TBody, TResp = unknown>(
  url: string | ((body: TBody) => string),
  method: "POST" | "PUT" | "DELETE" | "PATCH" = "POST",
  options?: UseApiMutationOptions<TBody, TResp>,
) {
  const queryClient = useQueryClient();
  const { invalidateKeys, timeoutMs, headers, body: bodyFn, onSuccess, ...rest } =
    options ?? {};

  return useMutation<TResp, Error, TBody, unknown>({
    mutationFn: (variables: TBody) => {
      const resolvedUrl =
        typeof url === "function" ? url(variables) : url;
      const init: RequestInit & { timeoutMs?: number } = { method };
      if (timeoutMs !== undefined) init.timeoutMs = timeoutMs;
      const requestHeaders = typeof headers === "function" ? headers(variables) : headers;
      if (requestHeaders !== undefined) init.headers = requestHeaders;
      const payload = bodyFn ? bodyFn(variables) : variables;
      if (payload !== undefined && payload !== null) {
        const mergedHeaders = new Headers(init.headers);
        if (!mergedHeaders.has("Content-Type")) {
          mergedHeaders.set("Content-Type", "application/json");
        }
        init.headers = mergedHeaders;
        init.body = JSON.stringify(payload);
      }
      return apiFetch<TResp>(resolvedUrl, init);
    },
    onSuccess: (data, variables, onMutateResult) => {
      if (invalidateKeys && invalidateKeys.length > 0) {
        for (const key of invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      }
      if (onSuccess) {
        onSuccess(data, variables, onMutateResult);
      }
    },
    ...rest,
  });
}
